/**
 * PublicEventDetailPage ✦ iter232 — /events/:slug
 *
 * SEO-first per-event landing page:
 *   • Hero (cover image, customer + venue, package label, ★ rating)
 *   • Gallery grid (real photos or branded placeholders)
 *   • Customer review block (when present)
 *   • Venue + package cross-links (turns into long-tail conversion)
 *   • Booking journey CTA ("Book this exact setup")
 *   • Related events (same venue OR same package)
 *   • Full <title>, <meta name="description">, og:*, canonical link
 *   • JSON-LD Event schema (Google rich-result eligible)
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import ServicesNav from '../components/services/ServicesNav';
import BrandedPlaceholder from '../components/services/BrandedPlaceholder';
import EnquiryForm from '../components/services/EnquiryForm';
import {
  SERIF, IVORY, IVORY_DIM, IVORY_FAINT,
  GOLD, GOLD_MUTED, GOLD_FAINT, GOLD_HAIRLINE,
  WALNUT_CARD, PAGE_BG, PILL_BUTTON_PRIMARY, PILL_BUTTON_GHOST,
} from '../components/services/_tokens';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// iter232 — SEO meta helper. Inserts / updates a meta tag without
// creating duplicates. Returns nothing.
const _setMeta = (name, content, isProperty = false) => {
  if (typeof document === 'undefined' || !content) return;
  const attr = isProperty ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const _setCanonical = (href) => {
  if (typeof document === 'undefined' || !href) return;
  let tag = document.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const _setJsonLd = (id, payload) => {
  if (typeof document === 'undefined') return;
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement('script');
    tag.setAttribute('type', 'application/ld+json');
    tag.setAttribute('id', id);
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(payload);
};

const mediaUrl = (path) => {
  if (!path) return null;
  return path.startsWith('/api/')
    ? `${process.env.REACT_APP_BACKEND_URL}${path}`
    : path;
};

const PACKAGE_LABELS = {
  'blossom-booth': 'Blossom Booth',
  'heritage-booth': 'Heritage Mirror Booth',
  'heritage-mirror-booth': 'Heritage Mirror Booth',
  'woodland-mirror-booth': 'Woodland Mirror Booth',
  'audio-guestbook': 'Audio Guestbook',
  'video-guestbook': 'Video Guestbook',
  'love-letters': 'LOVE Letters',
  'flower-wall': 'Flower Wall',
  classic: 'Classic Package',
  premium: 'Premium Package',
  ultimate: 'Ultimate Package',
  'print-booth': 'Print Booth',
};
const humanise = (slug) => slug && (PACKAGE_LABELS[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

const EVENT_TYPE_LABELS = {
  wedding: 'Wedding', corporate: 'Corporate Event', birthday: 'Birthday',
  anniversary: 'Anniversary', christmas_party: 'Christmas Party',
  school_prom: 'School Prom', charity: 'Charity', baby_shower: 'Baby Shower',
  engagement: 'Engagement', prom: 'Prom', other: 'Event',
};

const PublicEventDetailPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [venue, setVenue] = useState(null);
  const [related, setRelated] = useState([]);
  // iter232e — related reviews powered by /events/{slug}/related-reviews
  const [relatedReviews, setRelatedReviews] = useState([]);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public-events/${slug}`);
        if (cancelled) return;
        const ev = r.data.event;
        const v = r.data.venue;
        setEvent(ev);
        setVenue(v);

        // iter232 — SEO meta + JSON-LD. Title pattern:
        // "{customer/name} ✦ {event_type} at {venue} | EventDrop"
        const evType = humanise(ev.event_type) || EVENT_TYPE_LABELS[ev.event_type] || 'Event';
        const titleName = ev.title || ev.name || ev.customer_name || evType;
        const venueName = ev.venue_name || v?.name || '';
        const evDate = ev.event_date || ev.date || '';
        const fullTitle = venueName
          ? `${titleName} ✦ ${evType} at ${venueName} | EventDrop`
          : `${titleName} ✦ EventDrop`;
        document.title = fullTitle;
        const pkgLabel = humanise(ev.package_slug) || '';
        const desc = ev.review_text
          ? `${ev.review_text.slice(0, 200)}${ev.review_text.length > 200 ? '…' : ''}`
          : `Real ${evType.toLowerCase()} captured by EventDrop` +
            (venueName ? ` at ${venueName}` : '') +
            (evDate ? ` on ${evDate}` : '') +
            (pkgLabel ? ` using the ${pkgLabel}.` : '.') +
            ' Browse the photobooth gallery, customer review and book the same setup.';
        _setMeta('description', desc);
        _setMeta('og:title', fullTitle, true);
        _setMeta('og:description', desc, true);
        _setMeta('og:type', 'article', true);
        const hero = mediaUrl(ev.hero_image_url || ev.image_url || ev.cover_image);
        if (hero) _setMeta('og:image', hero, true);
        _setMeta('twitter:card', 'summary_large_image');
        _setMeta('twitter:title', fullTitle);
        _setMeta('twitter:description', desc);
        if (hero) _setMeta('twitter:image', hero);
        const canonical = `${window.location.origin}/events/${slug}`;
        _setMeta('og:url', canonical, true);
        _setCanonical(canonical);

        // JSON-LD Event schema → Google rich result eligible
        const ld = {
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: titleName,
          startDate: evDate || undefined,
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          description: desc,
          image: hero ? [hero] : undefined,
          location: venueName ? {
            '@type': 'Place',
            name: venueName,
            address: ev.location || ev.county || venueName,
          } : undefined,
          organizer: {
            '@type': 'Organization',
            name: 'EventDrop',
            url: window.location.origin,
          },
        };
        if (ev.review_text && ev.review_rating > 0) {
          ld.review = {
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: ev.review_rating,
              bestRating: 5,
            },
            author: {
              '@type': 'Person',
              name: ev.customer_name || 'EventDrop customer',
            },
            reviewBody: ev.review_text,
          };
        }
        _setJsonLd('event-jsonld', ld);

        // iter232 — Related events (same venue OR same package).
        try {
          const params = new URLSearchParams();
          if (ev.venue_slug) params.set('venue', ev.venue_slug);
          else if (ev.package_slug) params.set('package', ev.package_slug);
          if (params.toString()) {
            const r2 = await axios.get(`${API}/public-events?${params}&limit=8`);
            if (!cancelled) {
              const rel = (r2.data?.events || [])
                .filter(e => e.slug !== slug)
                .slice(0, 4);
              setRelated(rel);
            }
          }
        } catch { /* silent */ }

        // iter232e — Related reviews (venue → product → event-type).
        // Powers Craig's "every click should encourage another
        // click" engagement loop. One source of truth: the unified
        // /events/{slug}/related-reviews endpoint that walks the
        // Event's relationships in priority order.
        try {
          const rr = await axios.get(
            `${API}/events/${slug}/related-reviews?limit=4`,
          );
          if (!cancelled) {
            setRelatedReviews(rr.data?.reviews || []);
          }
        } catch { /* silent */ }
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const onShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ url, title: event?.title || event?.name }); } catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard unavailable */ }
    }
  };

  if (notFound) {
    return (
      <div data-testid="event-not-found" style={{ minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF }}>
        <ServicesNav />
        <div style={{ padding: 'clamp(100px, 16vw, 180px) 20px', textAlign: 'center' }}>
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 18, color: IVORY_DIM }}>
            That event isn't in our catalogue yet.
          </p>
          <Link to="/events" style={{ color: GOLD, marginTop: 18, display: 'inline-block' }}>
            See all events →
          </Link>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF }}>
        <ServicesNav />
        <div style={{ padding: 'clamp(100px, 16vw, 180px) 20px', textAlign: 'center' }}>
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM }}>The night is loading ✦</p>
        </div>
      </div>
    );
  }

  const titleName = event.title || event.name || event.customer_name || 'EventDrop event';
  const venueName = event.venue_name || venue?.name || '';
  const pkgSlug = event.package_slug;
  const pkgLabel = humanise(pkgSlug);
  const evDate = event.event_date || event.date || '';
  const evTypeLabel = EVENT_TYPE_LABELS[event.event_type] || 'Event';
  const heroUrl = mediaUrl(event.hero_image_url || event.image_url || event.cover_image);
  const gallery = (event.gallery_image_urls && event.gallery_image_urls.length > 0)
    ? event.gallery_image_urls
    : (event.gallery_images || event.gallery || []);

  return (
    <div data-testid="public-event-detail-page" style={{
      minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF,
    }}>
      <ServicesNav active="services" />

      <section style={{ padding: 'clamp(56px, 9vw, 100px) clamp(20px, 5vw, 60px) 0' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <Link to="/events" data-testid="event-back" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: IVORY_DIM, textDecoration: 'none',
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 13,
            marginBottom: 22,
          }}>← All events</Link>
          <BrandedPlaceholder
            label={titleName}
            sublabel={venueName}
            aspectRatio="21 / 9"
            imageUrl={heroUrl}
          />

          {/* Hero meta row */}
          <div style={{ marginTop: 26 }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD_MUTED, marginBottom: 8,
            }}>
              ✦ {evTypeLabel}{evDate ? ` · ${evDate.slice(0, 10)}` : ''}
            </div>
            <h1 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(30px, 5vw, 48px)', color: IVORY, margin: 0,
              letterSpacing: '-0.02em',
            }} data-testid="event-name">{titleName}</h1>
            {venueName && (
              <p style={{
                fontFamily: SERIF, fontStyle: 'italic', color: GOLD,
                fontSize: 14, margin: '10px 0 0', letterSpacing: '0.06em',
              }}>
                {venue ? (
                  <Link to={`/venues/${venue.slug || event.venue_slug}`}
                    data-testid="event-venue-link"
                    style={{ color: GOLD, textDecoration: 'underline', textDecorationColor: GOLD_FAINT }}>
                    {venueName}
                  </Link>
                ) : event.venue_slug ? (
                  <Link to={`/venues/${event.venue_slug}`} data-testid="event-venue-link"
                    style={{ color: GOLD, textDecoration: 'underline', textDecorationColor: GOLD_FAINT }}>
                    {venueName}
                  </Link>
                ) : venueName}
              </p>
            )}
            {(pkgLabel || event.review_rating > 0) && (
              <div style={{ marginTop: 10, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                {pkgLabel && (
                  <Link to={`/venues?package=${pkgSlug}`} data-testid="event-package-link"
                    style={{
                      fontFamily: SERIF, fontSize: 12.5, color: IVORY,
                      background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                      padding: '5px 12px', borderRadius: 999,
                      textDecoration: 'none', letterSpacing: '0.02em',
                    }}>{pkgLabel}</Link>
                )}
                {event.review_rating > 0 && (
                  <span data-testid="event-rating" style={{ color: GOLD, fontSize: 14, letterSpacing: 1.5 }}>
                    {'★'.repeat(Math.round(event.review_rating))}
                  </span>
                )}
              </div>
            )}
            {(event.cover_blurb || event.notes) && !event.review_text && (
              <p style={{
                fontFamily: SERIF, color: IVORY, fontSize: 16, lineHeight: 1.7,
                margin: '24px 0 0', maxWidth: 640,
              }}>{event.cover_blurb || ''}</p>
            )}
          </div>

          {/* Customer review */}
          {event.review_text && (
            <blockquote data-testid="event-review" style={{
              marginTop: 30, padding: '24px 26px',
              background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
              borderRadius: 12, maxWidth: 760,
            }}>
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
                color: GOLD, marginBottom: 10,
              }}>✦ Customer review</div>
              <p style={{
                fontFamily: SERIF, fontStyle: 'italic', color: IVORY,
                fontSize: 17, lineHeight: 1.6, margin: 0,
              }}>&ldquo;{event.review_text}&rdquo;</p>
              {event.customer_name && (
                <div style={{
                  fontFamily: SERIF, fontSize: 13, color: IVORY_DIM,
                  marginTop: 14, letterSpacing: '0.04em',
                }}>— {event.customer_name}</div>
              )}
            </blockquote>
          )}
        </div>
      </section>

      {/* Gallery */}
      {gallery.length > 0 ? (
        <section style={{ padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px) 0' }}>
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: GOLD_MUTED, marginBottom: 22, textAlign: 'center',
            }}>✦ Gallery</div>
            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }} data-testid="event-gallery">
              {gallery.slice(0, 24).map((url, i) => (
                <BrandedPlaceholder
                  key={i}
                  label={titleName}
                  sublabel={`Frame ${i + 1}`}
                  aspectRatio="1 / 1"
                  imageUrl={mediaUrl(url)}
                  testId={`event-gallery-${i}`}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* iter232e — Related reviews (Phase B "Review Everywhere") */}
      {relatedReviews.length > 0 && (
        <section data-testid="event-related-reviews" style={{
          padding: 'clamp(48px, 7vw, 80px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`, marginTop: 30,
        }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: GOLD_MUTED, marginBottom: 14, textAlign: 'center',
            }}>✦ More from EventDrop</div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(22px, 3vw, 28px)',
              textAlign: 'center', color: IVORY, margin: 0,
              letterSpacing: '-0.02em',
            }}>What other couples are saying</h2>
            <div style={{
              marginTop: 28, display: 'grid', gap: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            }}>
              {relatedReviews.map(r => (
                <Link key={r.review_id}
                  to={r.event_url || `/events/${r.event_slug || ''}`}
                  data-testid={`related-review-${r.review_id}`}
                  style={{
                    background: WALNUT_CARD,
                    border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 12, padding: '18px 20px',
                    textDecoration: 'none', color: IVORY,
                    display: 'block',
                  }}>
                  <div style={{ color: GOLD, fontSize: 16, letterSpacing: '0.04em' }}>
                    {'★'.repeat(r.rating || 5)}
                  </div>
                  <p style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 14, lineHeight: 1.5, color: IVORY_DIM,
                    margin: '10px 0 12px',
                  }}>&ldquo;{(r.review_text || '').slice(0, 140)}{(r.review_text || '').length > 140 ? '…' : ''}&rdquo;</p>
                  <div style={{
                    fontFamily: SERIF, fontSize: 12,
                    color: IVORY_FAINT, letterSpacing: '0.04em',
                  }}>
                    {r.customer_name}
                    {r.venue_name ? ` · ${r.venue_name}` : ''}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related events */}
      {related.length > 0 && (
        <section data-testid="event-related" style={{
          padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`, marginTop: 60,
        }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
              color: GOLD_MUTED, marginBottom: 14, textAlign: 'center',
            }}>✦ Related events</div>
            <h2 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(24px, 3.5vw, 32px)',
              textAlign: 'center', color: IVORY, margin: 0,
              letterSpacing: '-0.02em',
            }}>{event.venue_slug ? `More events at ${venueName || 'this venue'}` : 'Similar EventDrop events'}</h2>
            <div style={{
              marginTop: 30,
              display: 'grid', gap: 18,
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            }}>
              {related.map(r => (
                <Link key={r.slug} to={`/events/${r.slug}`}
                  data-testid={`event-related-${r.slug}`}
                  style={{
                    background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                    borderRadius: 12, overflow: 'hidden', textDecoration: 'none',
                    color: IVORY,
                  }}>
                  <BrandedPlaceholder
                    label={r.title || r.name}
                    sublabel={r.venue_name}
                    aspectRatio="4 / 3"
                    imageUrl={mediaUrl(r.hero_image_url || r.image_url || r.cover_image)}
                  />
                  <div style={{ padding: '14px 18px 18px' }}>
                    <h4 style={{
                      fontFamily: SERIF, fontWeight: 500, fontSize: 15,
                      margin: 0, letterSpacing: '-0.005em',
                    }}>{r.title || r.name}</h4>
                    {r.event_date && (
                      <div style={{ fontFamily: SERIF, fontStyle: 'italic',
                                    color: IVORY_DIM, fontSize: 12, marginTop: 4 }}>
                        {r.event_date.slice(0, 10)}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section style={{
        padding: 'clamp(60px, 8vw, 90px) clamp(20px, 5vw, 60px)',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD_MUTED, marginBottom: 12,
        }}>✦ Love this setup?</div>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM,
          fontSize: 14, maxWidth: 520, margin: '0 auto 22px',
          lineHeight: 1.55,
        }}>
          {pkgLabel
            ? `Bring the ${pkgLabel} to your night${venueName ? ` — the same setup as ${venueName}.` : '.'}`
            : 'Bring the same EventDrop magic to your night.'}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onShare}
            data-testid="event-share"
            style={PILL_BUTTON_GHOST}
          >Share this page</button>
          <button
            type="button"
            onClick={() => setEnquiryOpen(true)}
            data-testid="event-cta-enquire"
            style={PILL_BUTTON_PRIMARY}
          >Enquire for a similar night ✦</button>
        </div>
      </section>

      {enquiryOpen && (
        <EnquiryForm
          initialPackageSlug={pkgSlug || ''}
          onClose={() => setEnquiryOpen(false)}
          sourcePage={`event:${slug}`}
        />
      )}
    </div>
  );
};

export default PublicEventDetailPage;
